<template>
  <div class="stats-individual">
    <div class="page-header">
      <button class="btn-back" @click="$router.push({ name: 'InterventionPlan', query: { childID: $route.params.childID } })">← Back</button>
      <h1>Statistics - Individual</h1>
      <p v-if="studentName" class="student-name">{{ studentName }}</p>
    </div>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-else-if="plans.length === 0" class="empty">
      No intervention plans found for this student.
    </div>

    <div v-else class="content">
      <!-- Bar chart -->
      <div class="chart-card">
        <h2>Delta Days per IEP Goal</h2>
        <p class="chart-subtitle">Positive = overdue, Negative = completed early, 0 = on time</p>
        <VueApexCharts
          type="line"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>

      <!-- Estimated finish chart -->
      <div class="chart-card">
        <h2>Estimated Finish Date per IEP Goal</h2>
        <p class="chart-subtitle">Due date for each goal</p>
        <VueApexCharts
          type="line"
          height="300"
          :options="Object.assign({}, dateChartOptions, { colors: ['#2563eb'] })"
          :series="estimatedSeries"
        />
      </div>

      <!-- Actual finish chart -->
      <div class="chart-card">
        <h2>Actual Finish Date per IEP Goal</h2>
        <p class="chart-subtitle">Actual completion date (blank if not yet completed)</p>
        <VueApexCharts
          type="line"
          height="300"
          :options="Object.assign({}, dateChartOptions, { colors: ['#16a34a'] })"
          :series="actualSeries"
        />
      </div>

      <!-- Goal details table -->
      <div class="details-card">
        <h2>Goal Details</h2>
        <div class="goal-row" v-for="plan in plans" :key="plan.plan_id" @click="toggleGoal(plan.plan_id)">
          <div class="goal-header">
            <span class="goal-name">
              {{ expandedGoals[plan.plan_id] ? (plan.name || plan.goal) : truncate(plan.name || plan.goal, 60) }}
            </span>
            <span :class="['delta-badge', deltaBadgeClass(plan)]">{{ deltaLabel(plan) }}</span>
          </div>
          <div class="goal-meta">
            <div class="meta-item" v-if="plan.delay_reason">
              <span class="meta-label">Reason:</span>
              <span class="meta-value">{{ plan.delay_reason }}</span>
            </div>
            <div class="meta-item" v-if="plan.additional_info">
              <span class="meta-label">Additional info:</span>
              <span class="meta-value">{{ plan.additional_info }}</span>
            </div>
            <div class="meta-item" v-if="!plan.delay_reason && !plan.additional_info">
              <span class="meta-value muted">No reason or additional info recorded.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'SpecialistStatisticsIndividual',
  components: { VueApexCharts },
  data() {
    return {
      plans: [],
      studentName: '',
      loading: true,
      expandedGoals: {},
    };
  },
  computed: {
    sortedPlans() {
      return this.plans.slice().sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    },
    filteredPlans() {
      const deltas = this.sortedPlans.map(p => this.deltaDays(p)).filter(d => d !== null);
      if (deltas.length < 2) return this.sortedPlans;
      const mean = deltas.reduce((s, d) => s + d, 0) / deltas.length;
      const std = Math.sqrt(deltas.reduce((s, d) => s + Math.pow(d - mean, 2), 0) / deltas.length);
      return this.sortedPlans.filter(p => {
        const d = this.deltaDays(p);
        return d === null || Math.abs(d - mean) <= 2 * std;
      });
    },
    goalLabels() {
      return this.filteredPlans.map(p => this.formatDate(p.due_date));
    },
    chartSeries() {
      return [{ name: 'Delta Days', data: this.filteredPlans.map(p => this.deltaDays(p)) }];
    },
    chartOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5 },
        colors: ['#E7A63A'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.goalLabels,
          labels: { rotate: -30, style: { fontSize: '11px' } },
        },
        yaxis: { title: { text: 'Days' } },
        tooltip: {
          y: { formatter: val => val === null ? 'N/A' : `${val > 0 ? '+' : ''}${val}d` },
          x: {
            formatter: (val, { dataPointIndex }) => {
              const p = this.filteredPlans[dataPointIndex];
              return p ? this.truncate(p.name || p.goal, 40) : val;
            },
          },
        },
      };
    },
    estimatedSeries() {
      return [{ name: 'Estimated Finish', data: this.filteredPlans.map(p => p.due_date ? new Date(p.due_date).getTime() : null) }];
    },
    actualSeries() {
      return [{
        name: 'Actual Finish',
        data: this.filteredPlans.map(p => {
          const d = p.actual_completion_date ? new Date(p.actual_completion_date) : null;
          return d && !isNaN(d) ? d.getTime() : null;
        }),
      }];
    },
    dateChartOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5 },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.goalLabels,
          labels: { rotate: -30, style: { fontSize: '11px' } },
        },
        yaxis: {
          type: 'datetime',
          labels: { formatter: val => this.formatDate(new Date(val)) },
        },
        tooltip: {
          y: { formatter: val => val ? this.formatDate(new Date(val)) : 'N/A' },
        },
      };
    },
  },
  async mounted() {
    const childID = this.$route.params.childID;
    const base = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
    try {
      // Fetch plans
      const resp = await fetch(`${base}/api/intervention/child/${childID}`, { credentials: 'include' });
      if (resp.ok) this.plans = await resp.json() || [];

      // Fetch student name
      const childResp = await fetch(`${base}/api/child/${childID}`, { credentials: 'include' });
      if (childResp.ok) {
        const child = await childResp.json();
        this.studentName = `${child.first_name} ${child.last_name}`;
      }
    } catch (e) {
      console.error('StatisticsIndividual fetch error:', e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    toggleGoal(key) {
      this.$set(this.expandedGoals, key, !this.expandedGoals[key]);
    },
    deltaDays(plan) {
      if (!plan.due_date || !plan.actual_completion_date) return null;
      const due = new Date(plan.due_date);
      const end = new Date(plan.actual_completion_date);
      return Math.round((end - due) / 86400000);
    },
    deltaLabel(plan) {
      const d = this.deltaDays(plan);
      if (d === null) return 'N/A';
      if (d === 0) return 'On time';
      if (d > 0) return `${d}d overdue`;
      return `${Math.abs(d)}d early`;
    },
    deltaBadgeClass(plan) {
      const d = this.deltaDays(plan);
      if (d === null) return 'badge-na';
      if (d > 0) return 'badge-late';
      if (d < 0) return 'badge-early';
      return 'badge-ontime';
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.slice(0, len) + '…' : str;
    },
  },
};
</script>

<style scoped>
.stats-individual { padding: 24px; max-width: 1000px; margin: 50px auto 0; font-family: sans-serif; }
.page-header { margin-bottom: 24px; }
.btn-back { background: none; border: none; color: #aa6d27; cursor: pointer; font-size: 14px; padding: 0; margin-bottom: 8px; display: block; }
.btn-back:hover { text-decoration: underline; }
h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; color: #1F1F1F; }
.student-name { font-size: 18px; color: #a45902; margin: 0; font-weight: 600; }
.loading, .empty { color: #888; margin-top: 40px; text-align: center; font-size: 15px; }
.chart-card, .details-card { background: #fff; border: 1px solid #f0d9a8; border-radius: 10px; padding: 20px; margin-bottom: 24px; }
h2 { font-size: 16px; font-weight: 600; margin: 0 0 4px; color: #5c300b; }
.chart-subtitle { font-size: 12px; color: #c49040; margin: 0 0 16px; }
.goal-row { border-top: 1px solid #f0d9a8; padding: 12px 0; cursor: pointer; }
.goal-row:first-child { border-top: none; }
.goal-row:hover { background: #fff8ee; border-radius: 6px; }
.goal-header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.goal-name { font-weight: 600; font-size: 14px; flex: 1; color: #5c300b; }
.delta-badge { font-size: 12px; font-weight: 600; padding: 2px 8px; border-radius: 12px; }
.badge-late  { background: #aa6d27; color: #fff; }
.badge-early { background: #E7A63A; color: #fff; }
.badge-ontime { background: #f0d9a8; color: #aa6d27; }
.badge-na { background: #f3f4f6; color: #6b7280; }
.goal-meta { display: flex; flex-direction: column; gap: 4px; }
.meta-item { display: flex; gap: 6px; font-size: 13px; }
.meta-label { font-weight: 600; color: #aa6d27; white-space: nowrap; }
.meta-value { color: #1F1F1F; }
.muted { color: #c49040; font-style: italic; }
</style>
